// [PHASE 9B - NOV 20, 2025]: Added event subscriptions for reactive updates
import React, { useState, useReducer, useEffect } from 'react';
import { User, Users, Package, Coins, Settings, BookOpen, Shield, Zap, Book, Edit2, Save, ScrollText, Dices, Award, Moon, Calendar, Star, Castle, Clock, Hammer, Activity } from 'lucide-react';
import { useInventory } from '../contexts/InventoryContext';
import { useSettings } from '../contexts/SettingsContext';
import { systemBus, EVENTS } from '../services/systemBus';
import { effectCalculator } from '../services/effectCalculator';
import { Z } from '../constants/zLayers';
import { DISPLAY_MODES } from '../constants/enums';
// [STONE 10 / #80 — 2026-05-18]: Group the More-menu entries by their
//   tabGroups assignment. Same secondaryTabs in the same order — just
//   inserts a section header before each group's tabs. Skimmable nav
//   without changing tab order.
import { partitionTabsByGroup } from '../config/tabGroups';

const MobileNavigation = ({
  // [PHASE 4B - PARTIAL MIGRATION]: Keeping control props
  activeTab,
  setActiveTab,
  className = ""
}) => {
  // ============================================================================
  // PHASE 9B - NOV 20, 2025: EVENT SUBSCRIPTIONS
  // ============================================================================
  // [PURPOSE]: Force re-render mechanism for reactive updates
  // [WHY]: Track vault creation/removal and settings changes to update navigation
  // [INTEGRATION]: Follows pattern from Phase 6/9, works with context hooks
  // [NOTE]: Component uses context for data, subscriptions provide logging and cache clearing
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [PHASE 9B - NOV 20, 2025]: Subscribe to vault and settings events for navigation updates
  // [PURPOSE]: Re-render navigation when vault count or display mode changes
  // [WHY]: Vault count shown in label, display mode affects which tabs are visible
  useEffect(() => {
    const handleNavigationEvent = (eventName) => (data) => {
      effectCalculator.clearCache();
      forceUpdate();
    };

    // VAULT_CREATED: Re-render when vault created (update vault count in label)
    const unsubscribeVaultCreated = systemBus.on(EVENTS.VAULT_CREATED, handleNavigationEvent('VAULT_CREATED'));

    // VAULT_REMOVED: Re-render when vault removed (update vault count in label)
    const unsubscribeVaultRemoved = systemBus.on(EVENTS.VAULT_REMOVED, handleNavigationEvent('VAULT_REMOVED'));

    // SETTINGS_CHANGED: Re-render when settings change (display mode affects tabs)
    const unsubscribeSettingsChanged = systemBus.on(EVENTS.SETTINGS_CHANGED, handleNavigationEvent('SETTINGS_CHANGED'));

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeVaultCreated();
      unsubscribeVaultRemoved();
      unsubscribeSettingsChanged();
    };
  }, []); // Empty deps - subscribe once on mount

  // [PHASE 4B - MIGRATED]: Now using context hooks instead of props
  const { vaults = [] } = useInventory();
  const { settings } = useSettings();
  const displayMode = settings.displayMode || 'default';
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // [FIX - NOV 9, 2025]: Define all tabs based on display mode with THREE distinct modes
  // [WHY]: Default, Simple, and Just Inventory modes should show different features
  // [DISPLAYMODE]: 'default' = all features, 'simple' = hide DM tools, 'justInventory' = inventory only
  // [APR 2026 RE-ADDRESS]: Tightened to match the spec post-Pass A-D / Tier 2 / stack overhaul:
  //   - JUST_INVENTORY = "I just want to drag items around" → only character (compact),
  //     inventory, vault, log, settings. No spells/feats/skills/shop/dice/rest/party.
  //   - SIMPLE = play-the-game without character-build complexity. KEEP downtimes/calendar
  //     (timeSystem rework wired RestPanel + CalendarPanel together) and party. HIDE
  //     bastion (premium DM tool) + journal/tables/sources (campaign authoring).
  //   - DEFAULT = everything.

  let allTabs;

  if (displayMode === DISPLAY_MODES.JUST_INVENTORY) {
    // Just Inventory mode: drag-items-around use case. Trim everything except
    // the inventory grid + container access (vaults) + activity log + settings.
    // Character tab kept so compact CharacterIdentityHeader still has a home
    // (it shows Strength input + carry capacity that drives the grid size).
    allTabs = [
      { id: 'character', icon: User, label: 'Character', color: 'text-amber-400' },
      { id: 'inventory', icon: Package, label: 'Inventory', color: 'text-blue-400' },
      { id: 'vault', icon: Save, label: `Vaults (${vaults.length})`, color: 'text-cyan-400' },
      { id: 'log', icon: Edit2, label: 'Activity', color: 'text-indigo-400' },
      { id: 'settings', icon: Settings, label: 'Settings', color: 'text-gray-400' },
    ];
  } else if (displayMode === DISPLAY_MODES.SIMPLE) {
    // Simple mode: Player-focused, hide DM/build tools (no journal, tables,
    // sources, bastion). Keep core gameplay including calendar/downtimes/rest
    // since those are play-the-game surfaces tied to timeSystem.
    allTabs = [
      // Core character tabs
      { id: 'character', icon: User, label: 'Character', color: 'text-amber-400' },
      { id: 'inventory', icon: Package, label: 'Inventory', color: 'text-blue-400' },
      { id: 'spellbook', icon: Book, label: 'Spells', color: 'text-purple-400' },
      { id: 'skills', icon: Award, label: 'Skills', color: 'text-rose-400' },
      { id: 'feats', icon: Star, label: 'Feats', color: 'text-yellow-400' },
      // Commerce & Items (read-only shop browse — the Advanced Builder is gated
      // on hasPremiumAccess inside ShopTab itself)
      { id: 'shop', icon: Coins, label: 'Shop', color: 'text-green-400' },
      { id: 'magic', icon: Zap, label: 'Magic Items', color: 'text-cyan-400' },
      { id: 'vault', icon: Save, label: `Vaults (${vaults.length})`, color: 'text-cyan-400' },
      // Gameplay tools
      { id: 'dice', icon: Dices, label: 'Dice', color: 'text-pink-400' },
      { id: 'rest', icon: Moon, label: 'Rest', color: 'text-indigo-400' },
      { id: 'downtimes', icon: Clock, label: 'Downtimes', color: 'text-purple-400' },
      { id: 'calendar', icon: Calendar, label: 'Calendar', color: 'text-orange-400' },
      // Party
      { id: 'party', icon: Shield, label: 'Party', color: 'text-cyan-400' },
      // [PHASE B / #119 — F-004]: Surface Workshop entry in SIMPLE mode so
      // users can DISCOVER the headline feature exists. Muted color hints
      // it's Default-mode-only; the App.jsx workshop route already renders
      // a "switch to Default View" stub when displayMode !== DEFAULT.
      { id: 'workshop', icon: Hammer, label: 'Workshop', color: 'text-emerald-400/60' },
      // [2026-05-11]: Sources merged into Workshop tab — no longer a separate nav entry.
      // SIMPLE users access sourcebook toggles via Workshop tab (SourcesPanel renders
      // above ContentWorkshopSection outside the DEFAULT-only gate in App.jsx).
      // [ROSTER GALLERY 2026-05-10]: Roster tab — visual switcher for multi-character users
      { id: 'roster', icon: Users, label: 'Roster', color: 'text-amber-400' },
      // System
      { id: 'log', icon: Edit2, label: 'Activity Log', color: 'text-indigo-400' },
      { id: 'settings', icon: Settings, label: 'Settings', color: 'text-gray-400' },
    ];
  } else {
    // Default mode: All features including DM tools
    allTabs = [
      // Core character tabs
      { id: 'character', icon: User, label: 'Character', color: 'text-amber-400' },
      { id: 'inventory', icon: Package, label: 'Inventory', color: 'text-blue-400' },
      { id: 'spellbook', icon: Book, label: 'Spells', color: 'text-purple-400' },
      { id: 'skills', icon: Award, label: 'Skills', color: 'text-rose-400' },
      { id: 'feats', icon: Star, label: 'Feats', color: 'text-yellow-400' },
      // Commerce & Items
      { id: 'shop', icon: Coins, label: 'Shop', color: 'text-green-400' },
      { id: 'magic', icon: Zap, label: 'Magic Items', color: 'text-cyan-400' },
      { id: 'vault', icon: Save, label: `Vaults (${vaults.length})`, color: 'text-cyan-400' },
      // Gameplay tools
      { id: 'dice', icon: Dices, label: 'Dice', color: 'text-pink-400' },
      { id: 'rest', icon: Moon, label: 'Rest', color: 'text-indigo-400' },
      { id: 'downtimes', icon: Clock, label: 'Downtimes', color: 'text-purple-400' },
      { id: 'calendar', icon: Calendar, label: 'Calendar', color: 'text-orange-400' },
      { id: 'bastion', icon: Castle, label: 'Bastion', color: 'text-stone-400' },
      // DM/Campaign tools (only in default mode)
      { id: 'party', icon: Shield, label: 'Party', color: 'text-cyan-400' },
      // [SPEC D 2026-05-17]: Combat Manager — initiative + token canvas + CPI/SPI difficulty readout
      { id: 'combat', icon: Activity, label: 'Combat', color: 'text-red-400' },
      { id: 'journal', icon: ScrollText, label: 'Journal', color: 'text-orange-400' },
      { id: 'tables', icon: BookOpen, label: 'Roll Tables', color: 'text-purple-400' },
      { id: 'workshop', icon: Hammer, label: 'Workshop', color: 'text-emerald-400' },
      // [2026-05-11]: Sources merged into Workshop tab — no longer a separate nav entry.
      // [ROSTER GALLERY 2026-05-10]: Roster tab — visual switcher for multi-character users
      { id: 'roster', icon: Users, label: 'Roster', color: 'text-amber-400' },
      // System
      { id: 'log', icon: Edit2, label: 'Activity Log', color: 'text-indigo-400' },
      { id: 'settings', icon: Settings, label: 'Settings', color: 'text-gray-400' },
    ];
  }

  // [FIX JAN 2026 - Issue #5]: Filter tabs based on user hiddenTabs setting
  // Note: 'character', 'workshop', 'settings' are always visible (essential tabs).
  // Sources is now a section inside Workshop, so Workshop is the always-visible anchor.
  // [A17 — 2026-05-25]: Roster tab is gated on settings.enableRosterTab
  //   (default false) until the feature is finished. User report 5/17 —
  //   "Roster tab unsolicited + disconnected + broken — hide behind flag".
  //   Filtered here at the nav surface so it doesn't appear even if a user
  //   has 'roster' off their hiddenTabs list. The App.jsx render block is
  //   also gated on the same flag (belt-and-suspenders).
  const visibleTabs = allTabs.filter(tab => {
    if (tab.id === 'roster' && !settings.enableRosterTab) return false;
    return (
      ['character', 'workshop', 'settings'].includes(tab.id) ||
      !settings.hiddenTabs?.includes(tab.id)
    );
  });

  // For mobile navigation, split into primary and secondary.
  // Primary slot picks differ per mode because JUST_INVENTORY only has 5 total
  // tabs and doesn't include Spells/Shop.
  // [APR 2026 RE-ADDRESS]: Build primaryTabs by tab id (not array index) so
  // it stays correct when allTabs lists are reordered.
  const pickById = (ids) => ids
    .map(id => allTabs.find(t => t.id === id))
    .filter(Boolean);
  const primaryTabs = displayMode === DISPLAY_MODES.JUST_INVENTORY
    ? pickById(['character', 'inventory', 'vault', 'log']) // No Spells/Shop in JI
    : pickById(['character', 'inventory', 'spellbook', 'shop']);

  // [FIX JAN 2026 - Issue #5]: Use visibleTabs for secondary tabs (respects hiddenTabs setting)
  const secondaryTabs = visibleTabs.filter(tab =>
    !primaryTabs.some(primary => primary.id === tab.id)
  );

  // Mobile: Bottom navigation
  const MobileBottomNav = () => (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 md:hidden" style={{ zIndex: Z.NAV_BAR, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="flex">
        {primaryTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex flex-col items-center py-2 px-1 min-h-[60px] transition-colors ${
                isActive
                  ? `${tab.color} bg-gray-800`
                  : 'text-gray-400 hover:text-gray-200 active:bg-gray-800'
              }`}
              data-tutorial={`nav-tab-${tab.id}`}
            >
              <Icon size={20} className={`mb-1 ${isActive ? tab.color : ''}`} />
              <span className="text-xs font-medium truncate max-w-[60px]">{tab.label}</span>
            </button>
          );
        })}
        
        {/* More button for additional tabs */}
        <div className="relative flex-1">
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            data-testid="nav-more"
            className={`w-full flex flex-col items-center py-2 px-1 min-h-[60px] transition-colors ${
              secondaryTabs.some(tab => tab.id === activeTab) || showMoreMenu
                ? 'text-amber-400 bg-gray-800'
                : 'text-gray-400 hover:text-gray-200 active:bg-gray-800'
            }`}
          >
            <div className="w-5 h-5 mb-1 flex items-center justify-center">
              <div className="w-1 h-1 bg-current rounded-full mx-0.5"></div>
              <div className="w-1 h-1 bg-current rounded-full mx-0.5"></div>
              <div className="w-1 h-1 bg-current rounded-full mx-0.5"></div>
            </div>
            <span className="text-xs font-medium">More</span>
          </button>

          {/* [UI POLISH]: More menu — fixed positioning for full-width layout + backdrop dismiss */}
          {/* [STONE 10 / #80 — 2026-05-18]: Secondary tabs now render under
              group headers (Character / Items & Commerce / Gameplay /
              Party & Combat / Campaign Tools / Workshop & System) so the
              long list skims faster on mobile. Tab order within each group
              preserved; headers added only when ≥1 tab in that group. */}
          {showMoreMenu && (
            <>
              {/* Invisible click-catcher; the menu has no Escape handler, so this
                  backdrop is the only programmatic close. The testid is a load-bearing
                  handle for the sim's nav seam (simulation/actions/nav.ts). */}
              <div className="fixed inset-0" style={{ zIndex: Z.NAV_BAR }} data-testid="nav-more-backdrop" onClick={() => setShowMoreMenu(false)} />
              <div
                className="fixed bottom-[60px] left-2 right-2 mb-2 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-2 max-h-[60vh] overflow-y-auto"
                style={{ zIndex: Z.NAV_MENU }}
                data-testid="mobile-more-menu"
              >
                {partitionTabsByGroup(secondaryTabs).map(({ group, tabs }) => (
                  <div key={group.id} className="py-1" data-testid={`more-group-${group.id}`}>
                    <p className="px-4 py-1 text-[10px] uppercase tracking-wider text-amber-400 font-semibold border-b border-gray-700/50 mb-1">
                      {group.label}
                    </p>
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => {
                            setActiveTab(tab.id);
                            setShowMoreMenu(false);
                          }}
                          className={`w-full flex items-center px-4 py-3 text-sm transition-colors ${
                            isActive
                              ? `${tab.color} bg-gray-700`
                              : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          }`}
                          data-tutorial={`nav-tab-${tab.id}`}
                        >
                          <Icon size={18} className={`mr-3 ${isActive ? tab.color : ''}`} />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Desktop: Horizontal tabs (existing style, enhanced)
  const DesktopTabs = () => (
    <div className="hidden md:block mb-4">
      <div className="flex flex-wrap gap-2 justify-start items-center">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 rounded-lg transition-all duration-200 whitespace-nowrap flex items-center font-medium ${
                isActive
                  ? 'bg-amber-700 text-white shadow-lg transform scale-105'
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-200 hover:shadow-md'
              }`}
              data-tutorial={`nav-tab-${tab.id}`}
            >
              <Icon size={18} className="mr-2 flex-shrink-0" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={className}>
      <DesktopTabs />
      <MobileBottomNav />
    </div>
  );
};

export default MobileNavigation;